import { useFeature } from '@/hooks/use-feature';
import { FEATURES } from '@/hooks/use-feature';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';

export function VercelFeatureFlagPlayGround() {
  return (
    <div className="container mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Vercel Feature Flag Override Playground</h1>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>How to use this playground</AlertTitle>
          <AlertDescription>
            <div className="mb-2">
              This playground helps you test Vercel feature flag overrides in development:
            </div>
            <ol className="list-decimal list-inside space-y-1">
              <li>Make sure you&apos;re in development mode</li>
              <li>Look for the Vercel toolbar at the bottom of your screen</li>
              <li>Click on the &quot;Flags&quot; tab in the toolbar</li>
              <li>Toggle any feature flag to override its value</li>
              <li>Watch the cards below update in real-time</li>
            </ol>
          </AlertDescription>
        </Alert>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Object.entries(FEATURES).map(([key, value]) => (
          <FeatureCard key={key} name={key} flag={value} />
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ name, flag }: { name: string; flag: string }) {
  const isEnabled = useFeature(name as keyof typeof FEATURES);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{name}</span>
          <Badge variant={isEnabled ? 'default' : 'secondary'}>
            {isEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </CardTitle>
        <CardDescription>
          <span className="block">Feature flag: {flag}</span>
          <span className="block text-xs text-muted-foreground">
            This value can be overridden in the Vercel toolbar
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-2">
          <Switch checked={isEnabled} disabled />
          <Label>Current State</Label>
        </div>
      </CardContent>
    </Card>
  );
}
